import { memo, ChangeEvent } from "react";
import {
  Box,
  Typography,
  Button,
  Autocomplete,
  TextField,
  Grid,
  Chip,
  IconButton,
  Alert,
  Card,
  CardContent,
  Divider,
  Tooltip,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteIcon from "@mui/icons-material/Delete";
import WarningIcon from "@mui/icons-material/Warning";
import AddVariantTypeDialog from "./AddVariantTypeDialog.tsx";
import { useTheme } from "@mui/material/styles";
import {
  CON_MAX_VARIANT_COMBINATIONS,
  CON_VARIANT_COMBINATION_WARNING_THRESHOLD,
} from "../../utils/sys/Constants.tsx";
import { AutocompleteRenderInputParams } from "@mui/material/Autocomplete";

// Types and Interfaces
export interface Variant {
  type: string;
  values: string[];
}

export interface VariantTypeOption {
  value: string | number;
  label: string;
}

export interface VariantSectionProps {
  variants: Variant[];
  variantTypes: VariantTypeOption[];
  newVariantName: string;
  newVariantValue: string;
  showAddVariant: boolean;
  dialogVariantName: string;
  setNewVariantName: (value: string) => void;
  setNewVariantValue: (value: string) => void;
  setShowAddVariant: (show: boolean) => void;
  setDialogVariantName: (value: string) => void;
  handleAddNewVariant: () => void;
  handleRemoveVariantValue: (variantIndex: number, valueIndex: number) => void;
  handleRemoveVariant: (index: number) => void;
  canRemoveVariantValue?: (variantIndex: number, valueIndex: number) => boolean;
  canRemoveVariantType?: (index: number) => boolean;
  handleAddVariantType: (name: string, value: string) => void;
  handleAddVariantFromDialog: () => Promise<void>;
  handleKeyDownOnDropdown: (e: React.KeyboardEvent<HTMLDivElement>) => void;
  combinationCount: number;
  canAddVariants?: boolean;
  isEditMode?: boolean;
  styles: any;
  existingVariantTypesFromApi?: any[];
  loadingVariantTypes?: boolean;
}

// Extended theme interface
interface CustomTheme {
  palette: {
    warning: {
      main: string;
    };
    text: {
      primary: string;
    };
    greyscale: {
      stroke_1: string;
    };
    primary: {
      main: string;
      light: string;
    };
    error: {
      main: string;
      light: string;
      dark: string;
    };
    action: {
      disabled: string;
    };
    info: {
      main: string;
      light: string;
    };
  };
  shadows: string[];
}

const VariantSection = memo<VariantSectionProps>(
  ({
    variants,
    variantTypes,
    newVariantName,
    newVariantValue,
    showAddVariant,
    dialogVariantName,
    setNewVariantName,
    setNewVariantValue,
    setShowAddVariant,
    setDialogVariantName,
    handleAddNewVariant,
    handleRemoveVariantValue,
    handleRemoveVariant,
    canRemoveVariantValue = () => true,
    canRemoveVariantType = () => true,
    handleAddVariantFromDialog,
    handleKeyDownOnDropdown,
    combinationCount,
    canAddVariants = true,
    isEditMode = false,
    styles,
    existingVariantTypesFromApi = [],
    loadingVariantTypes = false,
  }) => {
    const theme = useTheme() as CustomTheme;

    // When editing: each variant type can have only one value (one combination per item)
    const selectedTypeAlreadyHasValue = 
      isEditMode && 
      variants.find((v) => v.type === newVariantName)?.values &&
      (variants.find((v) => v.type === newVariantName)?.values?.length ?? 0) >= 1;

    const wouldExceedLimit = (): boolean => {
      if (!newVariantName.trim() || !newVariantValue.trim()) return false;
      const existingVariant = variants.find((v) => v.type === newVariantName);
      if (existingVariant) {
        if (isEditMode && existingVariant.values.length >= 1) return true; // edit: one value per type
        if (existingVariant.values.includes(newVariantValue.trim())) return false;
        const updatedVariant = {
          ...existingVariant,
          values: [...existingVariant.values, newVariantValue.trim()],
        };
        const updatedVariants = variants.map((v) =>
          v.type === newVariantName ? updatedVariant : v
        );
        const newCount = updatedVariants.reduce((total, v) => total * (v.values?.length || 1), 1);
        return newCount > CON_MAX_VARIANT_COMBINATIONS;
      } else {
        const updatedVariants = [
          ...variants,
          { type: newVariantName, values: [newVariantValue.trim()] },
        ];
        const newCount = updatedVariants.reduce((total, v) => total * (v.values?.length || 1), 1);
        return newCount > CON_MAX_VARIANT_COMBINATIONS;
      }
    };

    const showWarning = combinationCount >= CON_VARIANT_COMBINATION_WARNING_THRESHOLD;
    const isAtLimit = combinationCount >= CON_MAX_VARIANT_COMBINATIONS;

    const getOptionLabel = (option: VariantTypeOption): string => option?.label || "";

    const handleVariantNameChange = (event: any, value: VariantTypeOption | null): void => {
      setNewVariantName(value?.label || "");
    };

    const handleNewVariantValueChange = (e: ChangeEvent<HTMLInputElement>): void => {
      setNewVariantValue(e.target.value);
    };

    const findVariantTypeValue = (): VariantTypeOption | null => {
      if (!newVariantName) return null;
      return variantTypes.find(
        (v) => v.label === newVariantName || String(v.value) === newVariantName
      ) || null;
    };

    return (
      <>
        <Box
          sx={{
            mb: 4,
            p: 3,
            border: "1px solid #4d4d4dff",
            borderRadius: 1,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
              mb: 2,
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: "bold" }}>
              Variant
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              {variants.length > 0 && showWarning && (
                <Typography variant="caption" sx={{ color: theme.palette.warning.main }}>
                  <WarningIcon sx={{ fontSize: 14, verticalAlign: "middle", mr: 0.5 }} />
                  {isAtLimit ? "Limit reached" : "Approaching limit"}
                </Typography>
              )}
              <Button
                variant="contained"
                startIcon={<AddCircleOutlineIcon />}
                size="small"
                sx={{
                  ...styles.btn_primary,
                  ...styles.btn_appbar_add,
                  height: 36,
                }}
                onClick={() => setShowAddVariant(true)}
              >
                Add Variant Type
              </Button>
            </Box>
          </Box>
          <Divider />

          {canAddVariants && (
          <Grid container spacing={3} alignItems="center" sx={{ mb: 1, pt: 2 }}>
            <Grid item xs={12} sm={5}>
              <Autocomplete
                options={variantTypes}
                getOptionLabel={getOptionLabel}
                value={findVariantTypeValue()}
                onChange={handleVariantNameChange}
                renderInput={(params: AutocompleteRenderInputParams) => (
                  <TextField
                    {...params}
                    label="Variant Name"
                    variant="outlined"
                    size="small"
                    fullWidth
                    onKeyDown={handleKeyDownOnDropdown}
                    inputProps={{
                      ...params.inputProps,
                      readOnly: true,
                    }}
                  />
                )}
                freeSolo={false}
                autoSelect={false}
              />
            </Grid>

            <Grid item xs={12} sm={5}>
              <TextField
                label="Value"
                variant="outlined"
                size="small"
                fullWidth
                value={newVariantValue}
                onChange={handleNewVariantValueChange}
                placeholder="Enter value (e.g., red)"
              />
            </Grid>
            <Grid item xs={12} sm={2}>
              <Button
                variant="contained"
                startIcon={<AddCircleOutlineIcon />}
                onClick={handleAddNewVariant}
                sx={{ ...styles.btn_primary, ...styles.btn_add_confirm }}
                disabled={selectedTypeAlreadyHasValue || !newVariantName || !newVariantValue || wouldExceedLimit() || isAtLimit}
                fullWidth
              >
                Add
              </Button>
            </Grid>
          </Grid>
          )}

          {variants.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 600,
                  mb: 2,
                  color: theme.palette.text.primary,
                }}
              >
                Variants
              </Typography>
              <Grid container spacing={2}>
                {variants.map((variant: Variant, variantIndex: number) => {
                  const variantTypeLabel = variantTypes.find(
                    (vt) => String(vt.value) === variant.type || vt.label === variant.type
                  )?.label || variant.type;
                  const allowTypeRemoval = canRemoveVariantType(variantIndex);

                  return (
                    <Grid item xs={12} sm={6} md={4} key={variantIndex}>
                      <Card
                        sx={{
                          border: `1px solid ${theme.palette.greyscale?.stroke_1 || '#E0E0E0'}`,
                          borderRadius: 2,
                          height: "100%",
                          display: "flex",
                          flexDirection: "column",
                          transition: "all 0.2s ease-in-out",
                          "&:hover": {
                            boxShadow: theme.shadows?.[4] || '0px 4px 8px rgba(0,0,0,0.1)',
                            borderColor: theme.palette.primary.main,
                          },
                        }}
                      >
                        <CardContent sx={{ flexGrow: 1, p: 2 }}>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "flex-start",
                            }}
                          >
                            <Box sx={{ flex: 1 }}>
                              <Typography
                                variant="subtitle1"
                                sx={{
                                  fontWeight: 600,
                                  color: theme.palette.text.primary,
                                }}
                              >
                                {variantTypeLabel}
                              </Typography>
                            </Box>
                            <Tooltip
                              title={
                                allowTypeRemoval
                                  ? "Remove variant type"
                                  : "Existing variants cannot be removed in edit mode"
                              }
                            >
                              <IconButton
                                size="small"
                                onClick={() => {
                                  if (allowTypeRemoval) {
                                    handleRemoveVariant(variantIndex);
                                  }
                                }}
                                disabled={!allowTypeRemoval}
                                sx={{
                                  color: allowTypeRemoval
                                    ? theme.palette.error.main
                                    : theme.palette.action.disabled,
                                  "&:hover": {
                                    backgroundColor: allowTypeRemoval
                                      ? (theme.palette.error.light || '#ffcdd2') + "20"
                                      : "transparent",
                                  },
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>

                          <Divider sx={{ my: 0.5 }} />

                          <Box>
                            <Box
                              sx={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 1,
                                mb: 2,
                                mt: 1,
                              }}
                            >
                              {variant.values.map((value: string, valueIndex: number) => {
                                const allowValueRemoval = canRemoveVariantValue(
                                  variantIndex,
                                  valueIndex
                                );
                                return (
                                  <Chip
                                    key={valueIndex}
                                    label={value}
                                    onDelete={
                                      allowValueRemoval
                                        ? () =>
                                            handleRemoveVariantValue(
                                              variantIndex,
                                              valueIndex
                                            )
                                        : undefined
                                    }
                                    variant="outlined"
                                    size="small"
                                    sx={{
                                      borderRadius: "16px",
                                      borderColor: theme.palette.primary.main,
                                      color: theme.palette.primary.main,
                                      backgroundColor: (theme.palette.primary.light || '#bbdefb') + "10",
                                      fontWeight: 500,
                                      "& .MuiChip-deleteIcon": {
                                        color: theme.palette.error.main,
                                        "&:hover": {
                                          color: theme.palette.error.dark,
                                        },
                                      },
                                      "&:hover": {
                                        backgroundColor: (theme.palette.primary.light || '#bbdefb') + "20",
                                      },
                                    }}
                                  />
                                );
                              })}
                            </Box>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            </Box>
          )}
          {showWarning && variants.length > 0 && (
            <Alert severity={isAtLimit ? "error" : "warning"} sx={{ mb: 2 }}>
              {isAtLimit
                ? `Maximum ${CON_MAX_VARIANT_COMBINATIONS} combinations reached. Remove some variant values to add more.`
                : `You have ${combinationCount} combinations. Maximum limit is ${CON_MAX_VARIANT_COMBINATIONS}.`}
            </Alert>
          )}
          {variants.length > 0 && (
            <Alert
              severity="info"
              sx={{
                mb: 2,
                backgroundColor: (theme.palette.info.light || '#b3e5fc') + "10",
                border: `1px solid ${theme.palette.info.main}40`,
              }}
            >
              <Typography variant="body2">
                <strong>{combinationCount}</strong> product combination
                {combinationCount !== 1 ? "s" : ""} will be created from these variants.
                {showWarning && (
                  <span>
                    {" "}
                    {isAtLimit
                      ? `Maximum limit of ${CON_MAX_VARIANT_COMBINATIONS} reached.`
                      : `Approaching maximum limit of ${CON_MAX_VARIANT_COMBINATIONS}.`}
                  </span>
                )}
              </Typography>
            </Alert>
          )}
        </Box>

        <AddVariantTypeDialog
          open={showAddVariant}
          onClose={() => setShowAddVariant(false)}
          existingVariantTypes={existingVariantTypesFromApi ?? []}
          loading={loadingVariantTypes}
          variantName={dialogVariantName ?? ""}
          onVariantNameChange={setDialogVariantName}
          onAdd={handleAddVariantFromDialog}
          styles={styles}
        />
      </>
    );
  }
);

VariantSection.displayName = "VariantSection";

export default VariantSection;